import * as ko from 'knockout';
import { CfbGameRecords } from './game-records';
import { RankMethod } from '../../shared/enums/rank-method';
import { Enumerable } from '../../shared/linq';
import type { IGame } from '../../shared/interfaces/IGame';
import type { IGameRecords } from '../../shared/interfaces/IGameRecords';
import type { ITeam } from '../../shared/interfaces/ITeam';

export class CfbTeam implements ITeam {
  TeamNameObs: KnockoutObservable<string>;
  isInit: boolean = false;
  TeamAbbreviation: string;
  TeamID: number;
  Division: string;
  Conference: string;
  TeamName: string;
  TeamDisplayName: string;
  ProgramName?: string;
  RegionName: string;
  TeamLogo: string;

  Wins: KnockoutObservableArray<IGame>;
  Losses: KnockoutObservableArray<IGame>;
  Ties: KnockoutObservableArray<IGame>;
  GameRecords: IGameRecords;

  Schedule: KnockoutObservable<Array<KnockoutObservable<IGame>>>;
  FakeObs: KnockoutObservable<any>;
  ConferenceRank: KnockoutObservable<number>;
  ConferenceCss: KnockoutComputed<string>;
  SelectedCss: KnockoutComputed<string>;
  SOV: KnockoutObservable<number>;
  SOS: KnockoutObservable<number>;
  SOSObj: KnockoutObservable<any>;
  SOVObj: KnockoutObservable<any>;
  DraftOrderRankMethod: KnockoutObservable<RankMethod>;
  DraftOrderRankMethodText: KnockoutComputed<string>;
  RankMethod: KnockoutObservable<RankMethod>;
  RankMethodText: KnockoutComputed<string>;
  ConferenceRankText: KnockoutComputed<string>;
  Tiebreakers: KnockoutObservable<any>;
  TeamChecked = false;
  DraftOrderNumber: KnockoutObservable<number>;
  IsReplaced: boolean;

  // ITeam: fractional SOS/SOV strings
  SOSFractional: KnockoutComputed<string>;
  SOVFractional: KnockoutComputed<string>;

  // Dynamic tiebreaker calc properties (set at runtime by ranking algo)
  HtHDivCalcPct?: number;
  BestCommonCalcPct?: number;
  BestCumulativeWinningPercentageOfNonDivisionalOpponents?: number;
  SkipRanking?: boolean;

  constructor(
    teamAbbreviation: string,
    regionName: string,
    teamName: string,
    conference: string,
    division: string,
    teamID: number,
    programName: string,
    teamLogo: string,
  ) {
    const self = this;
    self.TeamLogo = teamLogo;
    self.DraftOrderNumber = ko.observable(0);
    self.TeamAbbreviation = teamAbbreviation;
    self.RegionName = regionName;
    self.ProgramName = programName.replace(' ', '_');
    self.TeamNameObs = ko.observable(teamName);
    self.TeamName = self.TeamNameObs();
    self.Division = division;
    self.Conference = conference;
    self.TeamID = teamID;
    self.DraftOrderRankMethod = ko.observable(null);
    self.SOS = ko.observable(0);
    self.SOV = ko.observable(0);
    self.Tiebreakers = ko.observable({
      HTH: [],
      Sweeps: [],
      DivHTH: [],
    });
    self.Wins = ko.observableArray<IGame>([]);
    self.Losses = ko.observableArray<IGame>([]);
    self.Ties = ko.observableArray<IGame>([]);
    self.Schedule = ko.observable(new Array<KnockoutObservable<IGame>>());
    self.FakeObs = ko.observable();
    self.GameRecords = new CfbGameRecords(self.Wins, self.Losses, self.Ties, self.TeamNameObs, self.FakeObs);
    self.ConferenceRank = ko.observable<number>();
    self.RankMethod = ko.observable<RankMethod>(RankMethod.Unknown);

    self.DraftOrderRankMethodText = ko.pureComputed(() => {
      const m = self.DraftOrderRankMethod();
      if (m == null) {
        return '';
      }
      return self.RankMethodText();
    });

    self.RankMethodText = ko.pureComputed(() => {
      switch (self.RankMethod()) {
        case RankMethod.AllPCT: return 'Pct vs League';
        case RankMethod.ConfPCT: return 'Pct vs Conference';
        case RankMethod.DivPCT: return 'Pct vs Div';
        case RankMethod.SOS: return 'Strength of Schedule';
        case RankMethod.SOV: return 'Strength of Victory';
        case RankMethod.DivTop_AllPCT: return 'Top of Division - Pct vs League';
        case RankMethod.DivTop_DivPCT: return 'Top of Division - Pct vs Div';
        case RankMethod.DivTop_ConfPCT: return 'Top of Division - Pct vs Conference';
        case RankMethod.DivTop_SOS: return 'Top of Division - Strength of Schedule';
        case RankMethod.DivTop_SOV: return 'Top of Division - Strength of Victory';
        case RankMethod.DivTop_HTH: return 'Top of Division - Head to Head';
        case RankMethod.HTH: return 'Head-to-Head Tiebreaker';
        case RankMethod.CG: return 'Common Games';
        case RankMethod.CoinFlip: return 'Coin Flip';
        case RankMethod.CommonGames: return 'Common Games';
        case RankMethod.CommonPct: return 'Common Pct';
        case RankMethod.DivTop_CommonPct: return 'Top of Division - Common Pct';
        case RankMethod.CumulativeWinningPercentageOfNonDivisionalOpponents:
          return 'Cumulative Winning Percentage Of NonDivisional Opponents (tiebreaker 5)';
        default: return '';
      }
    });

    self.ConferenceCss = ko.pureComputed(() => {
      self.FakeObs();
      let cutoff = 2;
      if (self.Conference === 'Big Ten') {
        cutoff = 1;
      }
      return self.ConferenceRank() <= cutoff ? 'in-post-season' : 'out-post-season';
    });

    self.SelectedCss = ko.pureComputed(() =>
      self.TeamAbbreviation.replace(/\W/g, '_').replace('&', '_') + '_' + self.ProgramName.replace(/\W/g, '_'),
    );

    self.SOSObj = ko.observable<any>({ Wins: 0, Losses: 0, Ties: 0 });
    self.SOVObj = ko.observable<any>({ Wins: 0, Losses: 0, Ties: 0 });
    self.SOSFractional = ko.pureComputed(() => self.sosToString());
    self.SOVFractional = ko.pureComputed(() => self.sovToString());

    self.ConferenceRankText = ko.pureComputed(() => {
      self.FakeObs();
      let rank = '';
      if (self.ConferenceRank()) {
        rank += self.ConferenceRank();
        if (
          self.RankMethod() === RankMethod.SOS ||
          self.RankMethod() === RankMethod.CoinFlip ||
          self.RankMethod() === RankMethod.SOV
        ) {
          rank += '*';
        }
      }
      return rank;
    });
  }

  /** Win-Loss-Tie percentage; delegates to GameRecords.AllRecord */
  get Pct(): KnockoutComputed<number> {
    return this.GameRecords.AllRecord.Pct as KnockoutComputed<number>;
  }

  sosToString(): string {
    let retStr = `${this.SOSObj().Wins}-${this.SOSObj().Losses}`;
    if (this.SOSObj().Ties) retStr += `-${this.SOSObj().Ties}`;
    return retStr;
  }

  sovToString(): string {
    let retStr = `${this.SOVObj().Wins}-${this.SOVObj().Losses}`;
    if (this.SOVObj().Ties) retStr += `-${this.SOVObj().Ties}`;
    return retStr;
  }

  initSubscribers() {
    const self = this;
    if (!self.isInit) {
      self.Wins.subscribe((changes) => {
        changes.forEach((change) => {
          if (change.status === 'added') {
            self.Wins(Enumerable.From(self.Wins()).Distinct((p: any) => (p as any).gameScheduleNum).ToArray());
            self.Losses.remove((p: any) => (p as any).gameScheduleNum === (change.value as any).gameScheduleNum);
          }
        });
      }, null, 'arrayChange');

      self.Losses.subscribe((changes) => {
        changes.forEach((change) => {
          if (change.status === 'added') {
            self.Losses(Enumerable.From(self.Losses()).Distinct((p: any) => (p as any).gameScheduleNum).ToArray());
            self.Wins.remove((p: any) => (p as any).gameScheduleNum === (change.value as any).gameScheduleNum);
          }
        });
      }, null, 'arrayChange');

      self.Ties.subscribe((changes) => {
        changes.forEach((change) => {
          if (change.status === 'added') {
            self.Ties(Enumerable.From(self.Ties()).Distinct((p: any) => (p as any).gameScheduleNum).ToArray());
            self.Wins.remove((p) => (p as any).gameScheduleNum === (change.value as any).gameScheduleNum);
            self.Losses.remove((p) => (p as any).gameScheduleNum === (change.value as any).gameScheduleNum);
          }
        });
      }, null, 'arrayChange');

      (self.GameRecords as unknown as CfbGameRecords).initSubscribers();
      self.isInit = true;
    }
  }

  reset() {
    this.SOV(0);
    this.SOS(0);
    this.Wins([]);
    this.Losses([]);
    this.Ties([]);
    this.Schedule = ko.observable(new Array<KnockoutObservable<IGame>>());
    this.Tiebreakers({ HTH: [], Sweeps: [], DivHTH: [] });
    this.ConferenceRank(null);
  }
}
